import React, { useRef, useState, useEffect } from 'react';
import LabelPreview from './LabelPreview';
import './LabelThumb.css';

/*
  LabelThumb — miniatura clickable de la etiqueta para las filas de la tabla.

  Reutiliza el componente LabelPreview compartido a menor escala. Sólo renderiza
  la etiqueta real (con su QR) cuando la fila entra en el viewport
  (IntersectionObserver) — así una tabla con cientos de filas no genera cientos
  de QR de golpe. Mientras tanto muestra un placeholder del mismo tamaño para no
  provocar saltos de layout.

  Props:
    item        item de LabelPreview (qrValue, productName, variationLabel, …)
    config      { width, height } (mm)
    custom      boolean — si el producto ya tiene texto personalizado guardado
    onClick     () => void — abre el editor individual
    maxWidthPx  ancho máx de la miniatura (default 140)
    maxHeightPx alto máx de la miniatura (default 60)
*/
export default function LabelThumb({ item, config, custom, onClick, maxWidthPx = 140, maxHeightPx = 60 }) {
  const ref = useRef(null);
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    if (visible) return; // ya renderizado: no volver a observar
    const el = ref.current;
    if (!el) return;
    if (typeof IntersectionObserver === 'undefined') { setVisible(true); return; }
    const obs = new IntersectionObserver((entries) => {
      if (entries.some((e) => e.isIntersecting)) { setVisible(true); obs.disconnect(); }
    }, { rootMargin: '250px' });
    obs.observe(el);
    return () => obs.disconnect();
  }, [visible]);

  // Reserva el tamaño exacto del box (mismo cálculo que LabelPreview) para que el
  // placeholder no cause reflow al aparecer la etiqueta real.
  const w = Math.max(5, parseFloat(config?.width)  || 40);
  const h = Math.max(5, parseFloat(config?.height) || 12);
  const scale = Math.min(maxWidthPx / w, maxHeightPx / h);
  const boxW = Math.round(w * scale);
  const boxH = Math.round(h * scale);

  return (
    <button
      type="button"
      className="lt-thumb"
      onClick={onClick}
      title="Editar etiqueta"
      ref={ref}
    >
      <span className="lt-thumb__frame" style={{ width: boxW, height: boxH }}>
        {visible
          ? <LabelPreview item={item} config={config} maxWidthPx={maxWidthPx} maxHeightPx={maxHeightPx} />
          : <span className="lt-thumb__ph" style={{ width: boxW, height: boxH }} />}
      </span>
      <span className={`lt-thumb__tag${custom ? ' lt-thumb__tag--custom' : ''}`}>
        {custom ? '✎ Personalizada' : 'Sin personalizar'}
      </span>
    </button>
  );
}
